/**
 * Google Tag Manager integration utilities
 *
 * Events can be queued on `dataLayer` before the GTM script is injected.
 */

interface GTMConfig {
    id: string;
    serverUrl?: string;
    enabled: boolean;
    userId?: string | null;
    nonce?: string;
}

type GTMDataLayerEvent = Record<string, unknown>;
const PENDING_EVENTS_KEY = 'gtm.pending-events';
let pendingEventsRestored = false;

declare global {
    interface Window {
        dataLayer: GTMDataLayerEvent[];
    }
}

function dataLayer(): GTMDataLayerEvent[] {
    window.dataLayer = window.dataLayer || [];

    if (!pendingEventsRestored) {
        pendingEventsRestored = true;

        try {
            const pending = JSON.parse(sessionStorage.getItem(PENDING_EVENTS_KEY) ?? '[]') as unknown;

            if (Array.isArray(pending)) {
                window.dataLayer.push(...pending.filter((event): event is GTMDataLayerEvent => typeof event === 'object' && event !== null));
            }

            sessionStorage.removeItem(PENDING_EVENTS_KEY);
        } catch {
            try {
                sessionStorage.removeItem(PENDING_EVENTS_KEY);
            } catch {
                // Storage is unavailable; continue with the in-memory queue.
            }
        }
    }

    return window.dataLayer;
}

export function ensureDataLayer(userId?: string | null): void {
    const layer = dataLayer();

    if (userId) {
        layer.push({ user_id: userId });
    }
}

export function loadGTM(config: GTMConfig): void {
    if (!config.enabled || !config.id) {
        return;
    }

    const gtmUrl = config.serverUrl || 'https://www.googletagmanager.com';
    const src = `${gtmUrl}/gtm.js?id=${config.id}`;

    if (document.querySelector(`script[src="${src}"]`)) {
        return;
    }

    dataLayer().push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.dataset.gtmScript = 'true';
    script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
    });
    if (config.nonce) {
        script.nonce = config.nonce;
    }
    script.src = src;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `${gtmUrl}/ns.html?id=${config.id}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
}

export function persistPendingGTMEvents(): void {
    const script = document.querySelector<HTMLScriptElement>('script[data-gtm-script="true"]');

    if (script?.dataset.loaded === 'true' || !window.dataLayer?.length) {
        return;
    }

    try {
        sessionStorage.setItem(PENDING_EVENTS_KEY, JSON.stringify(window.dataLayer));
    } catch {
        // Storage may be unavailable; keep the in-memory queue intact.
    }
}

export function trackPageView(url: string, title?: string): void {
    dataLayer().push({
        event: 'page_view',
        page_location: url,
        page_title: title || document.title,
    });
}

const PUSHED_SERVER_EVENTS_KEY = 'gtm.pushed-server-events';
const pushedServerEventIds = new Set<string>();

export type ServerTrackingEvent = { id: string; event: string };

function wasServerEventPushed(id: string): boolean {
    if (pushedServerEventIds.has(id)) {
        return true;
    }

    try {
        const stored = JSON.parse(sessionStorage.getItem(PUSHED_SERVER_EVENTS_KEY) ?? '[]') as unknown;

        return Array.isArray(stored) && stored.includes(id);
    } catch {
        return false;
    }
}

function rememberServerEvent(id: string): void {
    pushedServerEventIds.add(id);

    try {
        const stored = JSON.parse(sessionStorage.getItem(PUSHED_SERVER_EVENTS_KEY) ?? '[]') as unknown;
        const ids = Array.isArray(stored) ? stored.filter((value): value is string => typeof value === 'string') : [];

        sessionStorage.setItem(PUSHED_SERVER_EVENTS_KEY, JSON.stringify([...ids, id].slice(-20)));
    } catch {
        // Storage is unavailable; the in-memory set still dedupes within this page.
    }
}

/**
 * Pushes events the server queued before a redirect, once per event id.
 * Inertia re-delivers the same props on history navigation.
 */
export function pushServerTrackingEvents(events: unknown): void {
    if (!Array.isArray(events)) {
        return;
    }

    for (const item of events as Partial<ServerTrackingEvent>[]) {
        if (typeof item?.id !== 'string' || typeof item.event !== 'string' || wasServerEventPushed(item.id)) {
            continue;
        }

        rememberServerEvent(item.id);
        pushEvent(item.event);
    }
}

export function pushEvent(event: string, data?: Record<string, unknown>): void {
    dataLayer().push({
        event,
        ...data,
    });
}
